import { useMutation, useQuery, useQueryClient } from 'react-query'
import axios from '../../utils/axios'
import { API_BASE_URL } from '../../constants'

// Operator panel for official GİB e-documents (e-Fatura / e-Arşiv).
// Every endpoint is is_superuser only on the backend, matching AdminGuard.

const QUEUE_KEY = 'ebelge-queue'

const fetchQueue = async (tab) => {
  const { data } = await axios.get(`${API_BASE_URL}/ebelge/queue`, { params: { tab } })
  return data
}

export const useEBelgeQueue = (tab) =>
  useQuery([QUEUE_KEY, tab], () => fetchQueue(tab), {
    refetchOnWindowFocus: false,
    // The backend tick runs hourly, so polling would add noise for nothing.
    // The table refreshes after any action instead.
    refetchInterval: false,
    keepPreviousData: true,
  })

/**
 * Render the document without filing it.
 *
 * Returns an object URL the caller opens in a new tab. Costs nothing at the
 * provider: no serial number is consumed and the row is not touched, so it is
 * safe to run as often as an operator likes.
 *
 * The date is passed through, so what they read is what would be filed.
 */
const buildOverrides = (issueDate, orderDate) => {
  const overrides = {}
  if (issueDate) overrides.issue_date = issueDate
  if (orderDate) overrides.order_date = orderDate
  return overrides
}

export const previewEBelge = async ({ id, issueDate, orderDate }) => {
  const response = await axios.post(
    `${API_BASE_URL}/ebelge/${id}/preview`,
    { overrides: buildOverrides(issueDate, orderDate) },
    { responseType: 'blob' },
  )
  return URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
}

/**
 * The preview endpoint answers with a PDF on success and JSON on failure, and
 * responseType 'blob' turns the error body into a Blob too. Read it back so the
 * operator gets the real Turkish reason rather than "[object Blob]".
 */
export const readBlobError = async (error, fallback) => {
  const data = error?.response?.data
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text())
      return parsed.error || fallback
    } catch {
      return fallback
    }
  }
  return data?.error || error?.message || fallback
}

/**
 * Extract the backend's Turkish message.
 *
 * The API answers 409 for "this row cannot be issued right now" and 502 for
 * "the provider call failed", and both carry a message written for an operator.
 * Losing it to a generic toast is how somebody presses Kes a second time.
 */
const messageFrom = (error, fallback) => error?.response?.data?.error || error?.message || fallback

export const useIssueEBelge = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({ id, issueDate, orderDate }) => {
      const { data } = await axios.post(`${API_BASE_URL}/ebelge/${id}/issue`, {
        // An object rather than named fields, so adding another editable value
        // later needs no change here.
        overrides: buildOverrides(issueDate, orderDate),
      })
      return data
    },
    {
      onSettled: () => queryClient.invalidateQueries(QUEUE_KEY),
      meta: { fallback: 'Fatura kesilemedi.' },
    },
  )
}

export const useSkipEBelge = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({ id, reason }) => {
      const { data } = await axios.post(`${API_BASE_URL}/ebelge/${id}/skip`, { reason })
      return data
    },
    { onSettled: () => queryClient.invalidateQueries(QUEUE_KEY) },
  )
}

export const useRefreshEBelge = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({ id }) => {
      const { data } = await axios.post(`${API_BASE_URL}/ebelge/${id}/refresh`)
      return data
    },
    { onSettled: () => queryClient.invalidateQueries(QUEUE_KEY) },
  )
}

export { messageFrom }

const BACKLOG_KEY = 'ebelge-backlog'

/**
 * Sales carrying no document at all.
 *
 * A separate query from the queue because it is a separate question. The queue
 * asks "which prepared documents need a decision"; this asks "which sales have
 * never been billed". Sharing a cache key would make an action on one silently
 * refetch the other under a label that does not describe it.
 */
const fetchBacklog = async () => {
  const { data } = await axios.get(`${API_BASE_URL}/ebelge/backlog`)
  return data
}

export const useEBelgeBacklog = (enabled) =>
  useQuery([BACKLOG_KEY], fetchBacklog, {
    enabled,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    keepPreviousData: true,
  })

/**
 * Bring one old sale into the lane.
 *
 * Prepares a document; it does NOT file one. The admitted row lands in the
 * pending queue and still needs a human to press Kes, so both caches are
 * invalidated: the sale leaves the backlog and appears in the queue.
 */
export const useAdmitEBelge = () => {
  const queryClient = useQueryClient()
  return useMutation(
    async ({ paymentPk, orderDate }) => {
      const { data } = await axios.post(`${API_BASE_URL}/ebelge/backlog/${paymentPk}/admit`, {
        order_date: orderDate,
      })
      return data
    },
    {
      onSettled: () => {
        queryClient.invalidateQueries(BACKLOG_KEY)
        queryClient.invalidateQueries(QUEUE_KEY)
      },
      meta: { fallback: 'Satış faturaya alınamadı.' },
    },
  )
}
